// The theme: System (follows the OS between Light and Dark) or one of THEMES (shared/src/api.ts). The choice is saved
// on the server (so it follows you to other devices; see /api/me/settings) and in localStorage, which the inline
// script in index.html reads before anything paints, so a page never flashes the wrong theme. Keep that script's
// logic in step with apply() here.
//
// <html data-palette> is the theme (styles.css has its variables) and <html data-theme> its base scheme, light or
// dark, which is all that the dark-only rules, app frames, card previews and native controls need to know.
import { useSyncExternalStore } from "react";
import { THEME_PREFS, themeInfo, type ThemeId, type ThemePref, type ThemeScheme } from "@bloom/shared";
import { api } from "./api.ts";

const KEY = "bloom:theme";

const dark = () => matchMedia("(prefers-color-scheme: dark)");
const listeners = new Set<() => void>();

function stored(): ThemePref {
  try {
    const v = localStorage.getItem(KEY) as ThemePref | null;
    return v && THEME_PREFS.includes(v) ? v : "system";
  } catch {
    return "system";
  }
}

let pref: ThemePref = stored();

/** The theme a preference shows right now (System resolves to Light or Dark). */
export const resolvedTheme = (p: ThemePref = pref): ThemeId => (p === "system" ? (dark().matches ? "dark" : "light") : p);
export const resolvedScheme = (p: ThemePref = pref): ThemeScheme => themeInfo(resolvedTheme(p)).scheme;

let dimmed = 0;

/** "#rrggbb" → [r, g, b]. */
const rgb = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const hex = (c: number[]) => `#${c.map((v) => Math.round(v).toString(16).padStart(2, "0")).join("")}`;

/**
 * The bar under a sheet's dimmed backdrop: the header colour seen through .sheet-backdrop's scrim
 * (rgb(var(--shade) / .4 * var(--scrim-k)), styles.css), worked out from the theme's own variables.
 */
function dimmedBar(bar: string): string {
  const cs = getComputedStyle(document.documentElement);
  const shade = cs.getPropertyValue("--shade").trim().split(/\s+/).map(Number);
  const a = Math.min(1, 0.4 * (parseFloat(cs.getPropertyValue("--scrim-k")) || 1));
  if (shade.length !== 3 || shade.some(Number.isNaN)) return bar;
  return hex(rgb(bar).map((v, i) => v * (1 - a) + shade[i]! * a));
}

function paintBar() {
  const { bar } = themeInfo(resolvedTheme());
  const color = dimmed > 0 ? dimmedBar(bar) : bar;
  // index.html has one per OS scheme (so it's right before scripts run); both follow the theme actually chosen.
  for (const m of document.querySelectorAll('meta[name="theme-color"]')) m.setAttribute("content", color);
}

/** While a bottom sheet dims the app, the phone's status bar dims with it (like a native sheet). Returns undo. */
export function dimBar(): () => void {
  dimmed++;
  paintBar();
  let done = false;
  return () => {
    if (done) return;
    done = true;
    dimmed--;
    paintBar();
  };
}

function apply() {
  const t = themeInfo(resolvedTheme());
  const root = document.documentElement;
  root.dataset.theme = t.scheme;
  root.dataset.palette = t.id;
  paintBar();
  for (const fn of listeners) fn();
}

/** Sets the theme on this device right away; `save` also stores it on the server for my other devices. */
export function setThemePref(next: ThemePref, { save = true } = {}) {
  if (!THEME_PREFS.includes(next)) return;
  pref = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // private mode: still applies for this page
  }
  apply();
  if (save) {
    picks++;
    void api("/me/settings", { method: "PUT", body: { themeName: next } }).catch(() => {});
  }
}

export const getThemePref = () => pref;
let picks = 0;
/** Counts choices made on this page, so an account answer that left before one doesn't undo it (session.tsx). */
export const themePicks = () => picks;

/** Re-applies when the OS switches between light and dark (only matters for "System"). */
export function installTheme() {
  apply();
  dark().addEventListener("change", () => pref === "system" && apply());
  // Another tab changed it.
  addEventListener("storage", (e) => {
    if (e.key !== KEY) return;
    pref = stored();
    apply();
  });
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** The chosen preference, the theme it shows right now, and that theme's base scheme. */
export function useTheme(): { pref: ThemePref; resolved: ThemeId; scheme: ThemeScheme } {
  const p = useSyncExternalStore(subscribe, () => pref);
  const resolved = useSyncExternalStore(subscribe, () => resolvedTheme());
  return { pref: p, resolved, scheme: themeInfo(resolved).scheme };
}
